"use client";

import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { getProductDiscounts } from 'app/actions/discount.actions';
import { BundleAddToCartItemInfo } from 'components/bundles/bundle-add-to-cart-item-info';
import { ButtonGreen } from 'components/buttons/default-green';
import Price from 'components/price';
import { useProduct } from 'components/product/product-context';
import { VariantSelector } from 'components/product/variant-selector';
import Prose from "components/prose";
import { ColorMetafield } from 'lib/shopify/types';
import { useEffect, useState } from 'react';
import { cn } from 'utils/cn';
import { CurrencyType, mapCurrencyToSign } from 'utils/mapCurrencyToSign';
import { formatTitle } from 'utils/slug-name';

export function ProductAccordion({bundleHandle, builderState}: {bundleHandle: any, builderState: any}) {
    const {selectedCamera, updateSelectedCamera} = useProduct()
    const product = builderState.state.shopifyProduct
    const isBundle = product.productType.toLowerCase() === 'bundle';
    const cameraList = bundleHandle.value.data.cameraList    

    const [openIndex, setOpenIndex] = useState<number | null>(isBundle ? -1 : 0);
    const [productDiscounts, setProductDiscounts] = useState<any>([]);    

    useEffect(() => {
        if(selectedCamera.handle === ''){
            updateSelectedCamera(cameraList[0]!)
        }
        const fetchProductDiscounts = async () => {
            const productDiscounts = await getProductDiscounts() as any;
            setProductDiscounts(productDiscounts);
        };
        fetchProductDiscounts();

        return () => {
            setProductDiscounts([])
            updateSelectedCamera({handle: '', includedInThisPackage: '', name: '', image: ''})
        }
    }, []);

    if(!bundleHandle) return <div>No product details data, please connect to a Builder.io product details data model</div>
    
    const properties = bundleHandle.value.data.properties
    const availableProperties = properties.filter((property: any) => property.includeInAccordion)

    const findProductDiscount = productDiscounts.find((discount: { customerGets: { items: { products: { edges: any[]; }; }; }; }) => discount.customerGets?.items?.products?.edges?.find((productDiscount: { node: { handle: string; }; }) => productDiscount.node.handle === product.handle))
    const discount = findProductDiscount?.customerGets?.value.percentage ?? 0
    const price = Number(product.priceRange.minVariantPrice.amount) ?? ''
    const subtotal = price * (1 - discount)
    const compareAtPrice = +product.compareAtPriceRange?.maxVariantPrice?.amount
    const calculateSaving = (1 - (subtotal / compareAtPrice)) * 100
    const currencyCode = mapCurrencyToSign(product.priceRange.minVariantPrice.currencyCode as CurrencyType) ?? ''
    const title = product.title.includes('System') ? product.title.split('System')[0] + 'System' : product.title;
    const edgelessColors = product.colors ? product.colors.references.edges.map((color: any) => color.node) as ColorMetafield[] : []
    const findIncludedInThisPackage = availableProperties.find((property: any) => 
        property.title.toLowerCase() === "included in this package"
    )
    if (findIncludedInThisPackage) {
        findIncludedInThisPackage.content = selectedCamera.includedInThisPackage
    }
    
    const toggleAccordion = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <div className="bg-white p-4 rounded-2xl">
            <h1 className="mb-2 text-4xl font-normal text-[36px] text-[#3d3e3d] tracking-[.4px] leading-[1.43]">{title}</h1>
            {!isBundle &&
                <>
                    <div className="mr-auto w-auto p-2 text-3xl font-medium text-[#3d3e3d]">
                        {isBundle ? 
                            <div>
                                <div className="mt-4 text-2xl flex items-center justify-center">
                                    <span className="text-[#008eaa]">Save {calculateSaving.toFixed(0)}%</span>
                                    <del 
                                        className="ml-4"
                                        aria-label={`Original price: ${currencyCode}${compareAtPrice}`}
                                    >
                                        {currencyCode}{compareAtPrice}
                                    </del>
                                </div>
                                <div className="my-4 font-bold flex items-center justify-center text-3xl">
                                    <ins 
                                        className="no-underline"
                                        aria-label={`Sale price: ${currencyCode}${Math.floor(subtotal).toFixed(2)}`}
                                    >
                                        {currencyCode}{Math.floor(subtotal).toFixed(2)}
                                    </ins>
                                </div>
                            </div> :
                            <Price
                                amount={product.priceRange.maxVariantPrice.amount}
                                currencyCode={product.priceRange.maxVariantPrice.currencyCode}
                            />
                        }
                    </div>
                    <div className="mt-2 mb-8">
                    <div className='mt-4'>
                        <VariantSelector options={product.options} variants={product.variants} colors={edgelessColors}/>
                    </div>
                        
                    <ButtonGreen href="/shop" className={cn('text-nowrap h-10 md:h-[65px] w-full md:w-2/5 justify-center')}>Shop Packages</ButtonGreen>
                    </div>
                </>
            }
            {isBundle && <BundleAddToCartItemInfo bundleHandle={bundleHandle} showPackagesButton={false} selectedCamera={selectedCamera} setSelectedCamera={updateSelectedCamera} />}
            
            {availableProperties?.map((item: {title: string, content: string}, index: number) => (
                <div key={index} className="">
                    <button
                        onClick={() => toggleAccordion(index)}
                        className={`flex w-full justify-between items-center py-4 px-2 text-left
                            ${openIndex === index - 1 ? 'border-t' : ''} ${index !== availableProperties.length - 1 ? 'border-b border-gray-200' : ''}`}
                    >
                        <h3 className="text-lg text-gray-900 font-bold">
                            {formatTitle(item.title)}
                        </h3>
                        <ChevronDownIcon 
                            className={`w-5 h-5 text-gray-500 transition-transform duration-200 
                                ${openIndex === index ? 'transform rotate-180' : ''}`}
                        />
                    </button>
                    
                    <div 
                        className={`text-gray-500 overflow-hidden transition-all duration-200 ease-in-out 
                            ${openIndex === index ? 'max-h-[1000px] py-4 px-2' : 'max-h-0'}`}
                    >
                        <Prose html={item.content} />
                    </div>
                </div>
            ))}
        </div>
    );
}

